<template>
  <main>
    <div class="py-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      <!-- Header -->
      <div class="md:flex md:items-center md:justify-between mb-5">
        <div class="flex-1 min-w-0">
          <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Blocks
          </h2>
          <p class="mt-1 text-sm text-gray-600">View your blocks and copy embed codes</p>
          <span v-if="currentUser" class="inline-block text-sm text-gray-600">{{blocks.length}} blocks</span>
        </div>
      </div>

      <div v-if="currentUser">
        <div v-if="!blocks.length && !isLoading">
          <div class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No blocks</h3>
            <p class="mt-1 text-sm text-gray-500">You don't have any blocks yet.</p>
          </div>
        </div>

        <div v-if="isLoading" class="max-w-7xl px-4 mx-auto sm:px-6 lg:px-8 text-center pt-2">
          <div class="inline-flex text-base leading-6 font-medium rounded-md text-gray-600 cursor-not-allowed">
            <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading...
          </div>
        </div>

        <!-- Blocks Grid -->
        <div v-if="blocks.length" class="mt-6 block-gallery-masonry">
          <div v-for="block in blocks" :key="block.id" class="block-gallery-item bg-white rounded-lg shadow divide-y divide-gray-200">
            <!-- Block Type Badge -->
            <div class="px-4 pt-3 pb-2">
              <div class="flex items-center justify-between">
                <span class="flex-shrink-0 inline-block px-2 py-0.5 text-xs font-medium rounded-full"
                      :class="'bg-' + block.color + '-100 text-' + block.color + '-800'">
                  {{ blockTypeLabel(block.type) }}
                </span>
                <div v-if="block.embedId" class="flex items-center text-xs text-green-600">
                  <svg class="flex-shrink-0 mr-1 h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  Embeddable
                </div>
              </div>
            </div>
            
            <!-- Block Preview -->
            <div class="px-3 pt-3 pb-3 bg-gray-50">
              <AutoFitPreview :padding="12" :min-scale="0.35" :max-scale="1.0" origin="top center">
                <BlockGalleryBlockPreview
                  :preset="block"
                  :id-prefix="`blocks_${block.id}`"
                  :index="0"
                />
              </AutoFitPreview>
            </div>
            
            <!-- Actions -->
            <div class="px-4 py-3 bg-gray-50">
              <div class="flex-shrink-0">
                <button v-if="block.embedId" @click="copyEmbedUrl(block)" type="button" class="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                  <svg class="-ml-0.5 mr-1.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clip-rule="evenodd" />
                  </svg>
                  Copy Embed
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Not logged in -->
      <div v-else class="py-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="rounded-md shadow-md bg-yellow-50 p-4 mb-5">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="border-2 border-yellow-200 h-12 w-12 text-yellow-400 bg-yellow-100 p-2 rounded" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3 flex-grow">
              <h3 class="text-md font-medium text-yellow-800">
                Sign up to create and manage blocks
              </h3>
              <div class="mt-1 text-sm text-yellow-700">
                <p>
                  Sign up to create blocks and get embed codes to use in Notion and other tools.
                </p>
              </div>
            </div>
            <div class="flex-shrink">
              <router-link
                to="signup"
                class="mt-2 inline-flex items-center px-3 py-2 border-2 border-yellow-200 text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >Start trial
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

  </main>
</template>

<script>
import { mapGetters } from "vuex";
import { getBlocks } from "../models/BlocksModel";
import { useToast } from "vue-toastification";
import AutoFitPreview from '@/components/AutoFitPreview.vue';
import BlockGalleryBlockPreview from '@/components/BlockGalleryBlockPreview.vue';

export default {
  name: 'Blocks',
  components: {
    AutoFitPreview,
    BlockGalleryBlockPreview,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      blocks: [],
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters("auth", ["currentUser"]),
  },
  mounted() {
    if (this.currentUser) {
      this.loadBlocks();
    }
  },
  methods: {
    loadBlocks() {
      this.isLoading = true;
      getBlocks()
        .then(resp => {
          let blocksData = resp.data;
          let blocks = [];
          blocksData.forEach(function (block) {
            if (block.data.isNew) delete block.data.isNew;
            block.data.refID = block.id;
            block.data.id = block.id;
            blocks.push(block.data);
          });
          this.$store.commit('blocksAdd', blocks);
          this.blocks = blocks;
          this.isLoading = false;
        })
        .catch(() => {
          this.toast.error(`Unable to fetch blocks from the database.`);
          this.isLoading = false;
        });
    },
    copyEmbedUrl(block) {
      const embedUrl = `${window.location.origin}/embed/${block.embedId}`;
      
      navigator.clipboard.writeText(embedUrl).then(() => {
        this.toast.success('Embed URL copied to clipboard!');
      }).catch(() => {
        this.toast.error('Failed to copy embed URL.');
      });
    },
    blockTypeLabel(type) {
      const labels = {
        'block': 'Timer',
        'counter': 'Counter',
        'countdown': 'Countdown',
        'progress': 'Progress',
        'collector': 'Collector',
        'link': 'Link',
        'todolist': 'Todo List',
      };
      return labels[type] || type;
    },
    createdDate(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return date.toLocaleDateString();
    }
  }
}
</script>

<style scoped>
.block-gallery-masonry {
  column-count: 1;
  column-gap: 16px;
}

@media (min-width: 640px) {
  .block-gallery-masonry {
    column-count: 2;
  }
}

@media (min-width: 1024px) {
  .block-gallery-masonry {
    column-count: 3;
  }
}

.block-gallery-item {
  display: inline-block;
  width: 100%;
  break-inside: avoid;
  -webkit-column-break-inside: avoid;
  margin: 0 0 16px;
}
</style>
